import { css } from 'lit';
import { UserPermissions } from '@typings/api';
import {
	AppSection,
	Breakpoint,
	CCTVGroup,
	CCTVView,
	DMSView,
	HHView,
	ImportsView,
	MILLISECONDS_PER_SECOND,
	MetricsView,
	NO_MONITOR_ID_INDICATOR,
	REMView,
	RampMeterView,
	Region,
	VSLView,
	WZRTView,
} from '../constants';
import { ConfigUserManagement } from './ConfigLogin';
import { MapRegion } from './config';

export enum APIHost {
	'LOCALHOST' = 'http://localhost:3000', //	local json-server for api response mocking
	'LOCAL_DEV' = 'http://localhost:8080/api/', //	local java server
	'DEV' = 'https://dev.irisx.org/api/',
	'TEST' = 'https://test.irisx.org/api/',
	'MA-STAGING' = 'https://api-carsx-ma.stage.carstest.org/api/',
	'MN-STAGING' = 'https://api-carsx-mn.stage.carstest.org/api/',
	'PGC-STAGING' = 'https://api-carsx-pgc.stage.carstest.org/api/',
	'NE-STAGING' = 'https://api-carsx-ne.stage.carstest.org/api/',
	'IN-CARSY-STAGE' = 'https://api-carsx-in.stage.carstest.org/api/',
	'CO-STAGING' = 'https://api-carsx-co.stage.carstest.org/api/',
	'MN-PREP' = 'https://api-carsx-mn.prep.carstest.org/api/',
	'MN-PROD' = 'https://api-carsx-mn.carsprogram.org/api/',
}

export const WZRT_TRAINING_MATERIALS_URL = 'https://sites.google.com/castlerockits.com/mn-wzrt-training/home';

export enum Districts {
	METRO_WEST = 'METRO WEST',
	METRO_EAST = 'METRO EAST',
	D1 = 'D1-DULUTH',
	D2 = 'D2-BEMIDJI',
	D3 = 'D3-BRAINERD',
	D4 = 'D4-DETROIT LAKES',
	D6 = 'D6-ROCHESTER',
	D7 = 'D7-MANKATO',
	D8 = 'D8-WILLMAR',
}

export enum MapLayerGroups {
	INCIDENTS = 'Incidents',
	SIGNS = 'Signs',
	CAMERAS = 'Cameras',
	RAMP_METERS = 'Ramp meters',
	IMPORTS = 'Imports',
	WORK_ZONES = 'Work zones',
}

export interface PageConfig {
	// pattern matching for route, specific match cases can be found in redux-routing.ts
	route: string;
	// concatted with ConfigCARSx.AppTitle for page title displayed in browser.
	pageTitle: string;
	// slug for url
	pageID?: string;
	// optional default views configurable by device size breakpoints
	// e.g. routing to map on mobile and table on desktop by default
	defaultRouteSegments?: {
		view: {
			[key in Breakpoint]: string;
		};
	};
	// permission to check before rendering in navbar and allowing routing
	permission?: UserPermissions;
	// if user DOES HAVE this permission, block them from the page
	disallowedPermission?: UserPermissions;
	// if false, no navbar icon or routing
	active?: boolean;
	// if false, won't render in navbar
	renderNavbar?: boolean;
	// icon to associate with route in navbar
	icon?: string;
	// optional icon dimensions
	iconWidth?: number;
	iconHeight?: number;
	// optional list of subpages which will be displayed in a dropdown if provided
	subpages?: PageConfig[];
}
export const ConfigCARSx = {
  AppTitle: 'CARSx', //  this will be used to populate the page title, e.g. 'AppTitle - PageTitle'

  Logo: {
    //  the logo is the first item of the left of the menu
    icon: 'logo.png',
    alt: 'Minnesota Department of Transportation',
    href: '/', //  clicking the link goes here,
  },

	Pages: {
		[AppSection.LOGIN]: {
			route: '/login',
			pageTitle: 'Login',
			renderNavbar: false,
		},
		[AppSection.CCTV]: {
			route: '/cctv', //  pattern matching for the route
			pageID: 'cctv',
			defaultRouteSegments: {
				// if you go to cctv/lobby/ - omitting the view value - the app will default
				// to different paths depending on current layout type:
				view: {
					[Breakpoint.desktop]: 'table',
					[Breakpoint.mobile]: 'table',
				},
			},
			pageTitle: 'CCTV', //  concatted with AppTitle and used as the <title> displayed in the browser
			permission: UserPermissions.CCTV_ACCESS,
			active: false,
			renderNavbar: false,
			icon: 'img/cctv-topbar-icon.svg',
			subpages: [
				{
					pageTitle: 'Camera Control',
					route: `/cctv/${NO_MONITOR_ID_INDICATOR}/${CCTVView.table}`,
					permission: UserPermissions.CCTV_ACCESS,
				},
				{
					pageTitle: 'Camera Inventory Tool',
					route: `/cctv/${CCTVGroup.inventory}/${CCTVView.table}`,
					permission: UserPermissions.CCTV_CAN_MANAGE_INVENTORY,
				},
			],
		},
		[AppSection.DMS]: {
			route: '/dms',
			pageID: 'dms',
			defaultRouteSegments: {
				view: {
					[Breakpoint.desktop]: 'table',
					[Breakpoint.mobile]: 'table',
				},
			},
			pageTitle: 'DMS',
			permission: UserPermissions.DMS_ACCESS,
			active: false,
			renderNavbar: false,
			icon: 'img/icon-sign-fill-solid.svg',
			subpages: [
				{
					pageID: 'table',
					pageTitle: 'Sign List',
					route: `/dms/${DMSView.table}`,
					permission: UserPermissions.DMS_ACCESS,
				},
				{
					pageID: 'table',
					pageTitle: 'Sign Map',
					route: `/dms/${DMSView.map}`,
					permission: UserPermissions.DMS_ACCESS,
				},
				{
					pageID: 'groups',
					pageTitle: 'Sign Groups',
					route: `/dms/${DMSView.group}`,
					permission: UserPermissions.DMS_CAN_MANAGE_GROUPS,
				},
				{
					pageID: 'messages',
					pageTitle: 'Custom Messages',
					route: `/dms/${DMSView['custom-messages']}`,
					permission: UserPermissions.DMS_CAN_MANAGE_MESSAGES,
				},
				{
					pageID: 'images',
					pageTitle: 'Sign Graphics',
					route: `/dms/${DMSView['image-library']}`,
					permission: UserPermissions.DMS_CAN_MANAGE_GRAPHICS,
				},
			],
		},
		[AppSection.VSL]: {
			route: `/${AppSection.VSL}`,
			pageID: AppSection.VSL,
			pageTitle: 'VSL',
			permission: UserPermissions.VSL_ACCESS,
			renderNavbar: false,
			icon: 'img/icon-vsl-icon-solid.svg',
			subpages: [
				{
					pageTitle: 'VSL List',
					route: `/${AppSection.VSL}/${VSLView.TABLE}`,
					permission: UserPermissions.VSL_ACCESS,
				},
				{
					pageTitle: 'VSL Map',
					route: `/${AppSection.VSL}/${VSLView.MAP}`,
					permission: UserPermissions.VSL_ACCESS,
				},
			],
			active: false,
		},
		[AppSection.REM]: {
			route: '/rem',
			pageID: 'rem',
			defaultRouteSegments: {
				view: {
					[Breakpoint.desktop]: 'table',
					[Breakpoint.mobile]: 'table',
				},
			},
			pageTitle: 'Events',
			permission: UserPermissions.REM_ACCESS,
			active: true,
			renderNavbar: true,
			icon: 'img/icon-rem-fill-solid.svg',
			subpages: [
				{
					pageTitle: 'Incident List',
					route: `/rem/${REMView.table}`,
					permission: UserPermissions.REM_ACCESS,
				},
				{
					pageTitle: 'Incident Map',
					route: `/rem/${REMView.map}`,
					permission: UserPermissions.REM_ACCESS,
				},
				{
					pageTitle: 'New Incident',
					route: `/rem/${REMView.event}/new`,
					permission: UserPermissions.REM_ACCESS,
					disallowedPermission: UserPermissions.REM_READONLY,
				},
			],
		},
		[AppSection.HH]: {
			route: '/highway-helper',
			pageID: 'highway-helper',
			defaultRouteSegments: {
				view: {
					[Breakpoint.desktop]: 'table',
					[Breakpoint.mobile]: 'table',
				},
			},
			pageTitle: 'Safety Service Patrol',
			permission: UserPermissions.HH_ACCESS,
			active: false,
			renderNavbar: false,
			icon: 'img/icon-helper-fill-solid-module.svg',
			subpages: [
				{
					pageID: 'ssp-patrol-tracker',
					pageTitle: 'Patrol Tracker',
					route: `/highway-helper/${HHView.table}`,
				},
				{
					pageID: 'ssp-admin-tool',
					pageTitle: 'SSP Admin',
					route: `/highway-helper/${HHView.sspAdmin}`,
					permission: UserPermissions.SSP_ADMIN_ACCESS,
				},
			],
		},
		[AppSection.METRICS]: {
			route: '/metrics',
			pageID: 'metrics',
			defaultRouteSegments: {
				view: {
					[Breakpoint.desktop]: 'events',
					[Breakpoint.mobile]: 'events',
				},
			},
			pageTitle: 'Metrics',
			permission: UserPermissions.METRICS_ACCESS,
			active: true,
			renderNavbar: true,
			icon: 'img/icon-metric-fill-solid-module.svg',
			subpages: [
				{
					pageID: 'metrics-events',
					pageTitle: 'Events',
					route: `/metrics/${MetricsView.events}`,
				},
				/*{
					pageID: 'metrics-helpers',
					pageTitle: 'Safety Service Patrol',
					route: `/metrics/${MetricsView.helpers}`,
				},
				{
					pageID: 'metrics-dms',
					pageTitle: 'Sign Message History',
					route: `/metrics/${MetricsView.dms}`,
				},
				{
					pageID: 'performance-dashboard',
					pageTitle: 'KPI Dashboard',
					route: `/metrics/${MetricsView.dashboard}`,
					active: false,
				},*/
			],
		},
		[AppSection.RAMP_METER]: {
			route: '/rampmeter',
			pageID: 'rampmeter',
			defaultRouteSegments: {
				view: {
					[Breakpoint.desktop]: 'table',
					[Breakpoint.mobile]: 'table',
				},
			},
			pageTitle: 'Ramp Meter',
			permission: UserPermissions.RAMP_METER_ACCESS,
			active: false,
			renderNavbar: false,
			icon: 'img/icon-ramp-meter-navigations.svg',
			subpages: [
				{
					pageTitle: 'Ramp Meter List',
					route: `/rampmeter/${RampMeterView.table}`,
					permission: UserPermissions.CCTV_ACCESS,
				},
				{
					pageTitle: 'Ramp Meter Map',
					route: `/rampmeter/${RampMeterView.map}`,
					permission: UserPermissions.CCTV_ACCESS,
				},
			],
		},
		[AppSection.LINKS]: {
			route: '',
			pageID: 'Links',
			pageTitle: 'Links',
			permission: UserPermissions.REM_ACCESS,
			active: true,
			renderNavbar: true,
			icon: 'img/icon-menu-fill.svg',
			// configure additional external links below
			subpages: [
				        {
          pageID: 'links-cms',
          pageTitle: 'Content Management System',
          route: 'https://cms.511mn.org/',
          active: true,
        },
        {
          pageID: 'links-tgseg',
          pageTitle: 'TG Segments',
          route: 'https://mn.carsprogram.org/segments_v1/index-retro.jsp',
          active: true,
        },
        {
          pageID: 'links-segdef',
          pageTitle: 'Segment Definition Tool',
          route: 'https://mn.carsprogram.org/MNsegmentdefinition/secure/segmentManagement.jsf',
          active: true,
        },
        {
          pageID: 'links-training',
          pageTitle: 'Training',
          route: 'https://sites.google.com/castlerockits.com/training-mn-carsx/home',
          active: true,
        },
		{
		pageID: 'links-ow',
		pageTitle: 'OneWeb Public Website',
		route: `https://511mn.org/`,
		},
			],
		},
		[AppSection.IMPORTS]: {
			route: '/imports',
			pageID: 'imports',
			defaultRouteSegments: {
				view: {
					[Breakpoint.desktop]: 'table',
					[Breakpoint.mobile]: 'table',
				},
			},
			pageTitle: 'Imports',
			permission: UserPermissions.IMPORTS_ACCESS,
			active: true,
			renderNavbar: true,
			icon: 'img/cad-icon.svg',
			subpages: [
				{
					pageTitle: 'Imports List',
					route: `/imports/${ImportsView.table}`,
					permission: UserPermissions.IMPORTS_ACCESS,
				},
				{
					pageTitle: 'Imports Map',
					route: `/imports/${ImportsView.map}`,
					permission: UserPermissions.IMPORTS_ACCESS,
				},
			],
		},
		[AppSection.USER_GROUPS]: {
			route: '/users',
			pageID: 'users',
			pageTitle: 'User Groups',
			renderNavbar: false,
			active: ConfigUserManagement.showManageUsersPage,
			permission: UserPermissions.USER_MANAGEMENT_ACCESS,
		},
		[AppSection.WZRT]: {
			route: '/wzrt',
			icon: 'img/icon-workzone-fill.svg',
			iconWidth: 32,
			iconHeight: 32,
			pageID: AppSection.WZRT,
			pageTitle: 'Work Zones',
			renderNavbar: true,
			active: true,
			permission: UserPermissions.WZRT_ACCESS,
			subpages: [
				{
					pageTitle: 'Work Zone List',
					route: `/wzrt/${WZRTView.table}`,
					permission: UserPermissions.WZRT_ACCESS,
				},
				{
					pageTitle: 'Work Zone Map',
					route: `/wzrt/${WZRTView.map}`,
					permission: UserPermissions.WZRT_ACCESS,
				},
				{
					pageTitle: 'New Work Zone',
					route: `/wzrt/${WZRTView.event}/new`,
					permission: UserPermissions.WZRT_CAN_CREATE,
				},
				WZRT_TRAINING_MATERIALS_URL != null
					? {
							pageTitle: 'Training Materials',
							route: WZRT_TRAINING_MATERIALS_URL,
							permission: UserPermissions.WZRT_ACCESS,
						}
					: {},
			],
		},
  } as Record<AppSection, PageConfig>,
  defaultPage: AppSection.REM,
  Map: {
    //  map location search will prioritize results by proximity to this point:
    GeoSearch: {
      APIRoot: 'https://photon.komoot.io/api/',
      BBox: [-97.239209, 43.499356, -89.491739,  49.384358],
      BaseSearch: 'Minnesota',
      ResultsLanguage: 'en',
      SearchLocation: { lat: 42.407211, lon: -71.382439 },
    },
    Regions: [
      {
        name: 'D-METRO',
        bounds: {
          EastLon: -92.64645114837985,
          NorthLat: 45.73102664523775,
          WestLon: -94.0104345170877,
          SouthLat: 44.47183188282664,
        },
      },
      {
        name: 'D1-DULUTH',
        bounds: {
          EastLon: -89.49177084262756,
          NorthLat: 48.64281727172504,
          WestLon: -93.8201889694364,
          SouthLat: 45.7300165829393,
        },
      },
      {
        name: 'D2-BEMIDJI',
        bounds: {
          EastLon: -93.57511307362836,
          NorthLat: 49.383751141583076,
          WestLon: -97.23909269224801,
          SouthLat: 46.75398302342679,
        },
      },
      {
        name: 'D3-BRAINERD',
        bounds: {
          EastLon: -93.01958290166363,
          NorthLat: 47.32643234308878,
          WestLon: -95.18155792462056,
          SouthLat: 44.90573782131681,
        },
      },
      {
        name: 'D4-DETROIT LAKES',
        bounds: {
          EastLon: -95.09493006383786,
          NorthLat: 47.542636270104005,
          WestLon: -96.85743440119147,
          SouthLat: 45.08016369359273,
        },
      },
      {
        name: 'D6-ROCHESTER',
        bounds: {
          EastLon: -91.21735827717522,
          NorthLat: 44.71389094446327,
          WestLon: -93.64893499739655,
          SouthLat: 43.499384145537434,
        },
      },
      {
        name: 'D7-MANKATO',
        bounds: {
          EastLon: -93.38668106821258,
          NorthLat: 44.76815226323235,
          WestLon: -96.45350422702496,
          SouthLat: 43.49963965135414,
        },
      },
      {
        name: 'D8-WILLMAR',
        bounds: {
          EastLon: -93.8699712405861,
          NorthLat: 45.45279514422455,
          WestLon: -96.45338644102982,
          SouthLat: 43.834976203004516,
        },
      },
    ] as MapRegion[],
		Layers: {
			[MapLayerGroups.CAMERAS]: {
				icon: 'img/cctv-topbar-icon.svg',
				active: true,
				togglable: true,
				appSection: AppSection.CCTV,
			},
			[MapLayerGroups.INCIDENTS]: {
				icon: 'img/icon-rem-fill-solid.svg',
				active: true,
				togglable: false,
				appSection: AppSection.REM,
			},
			[MapLayerGroups.SIGNS]: {
				icon: 'img/icon-sign-fill-solid.svg',
				active: true,
				togglable: true,
				appSection: AppSection.DMS,
			},
			[MapLayerGroups.RAMP_METERS]: {
				icon: 'img/icon-ramp-meter-navigations.svg',
				active: true,
				togglable: true,
				appSection: AppSection.RAMP_METER,
			},
			[MapLayerGroups.IMPORTS]: {
				icon: 'img/cad-icon.svg',
				active: true,
				togglable: true,
				appSection: AppSection.IMPORTS,
			},
			[MapLayerGroups.WORK_ZONES]: {
				icon: 'img/icon-workzone-fill.svg',
				active: true,
				togglable: false,
				appSection: AppSection.WZRT,
			},
		},
	},
	showWorkZoneLogin: window.location.hostname === 'wzrequest.511mn.org',
	showAlerts: false,
};
export default ConfigCARSx;

//  https://docs.mapbox.com/help/troubleshooting/migrate-legacy-static-tiles-api/
export const leafletConfig = {
  tileUrlTemplate:
    'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
  satelliteLayerId: 'mapbox/satellite-streets-v11',
  streetsLayerId: 'mapbox/streets-v11',
  tileLayerOptions: {
    id: 'mapbox/traffic-day-v2',
    minZoom: 0,
    maxZoom: 22,
    zoomOffset: -1,
    tileSize: 512,
    accessToken:
      'pk.eyJ1IjoiY3JjLWRldiIsImEiOiJjaWY1b3NwOXcwMmtkc2trdDExOTA3Nmh2In0.eVQGwyWVr-zTFF5oyFU5wA',
      
    attribution:
      '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
  },
  scaleControlOptions: {
    metric: true,
    imperial: true,
  },
};

export const tableConfig = {
  paginationSizes: [5, 15, 50, 100],
  defaultPagination: 3,
};

const APIConfig = {
  relativeAPIPath: APIHost['MN-PROD'],
  endpointURLBase: '/',
  requestTimeoutMs: 120 * MILLISECONDS_PER_SECOND,
  eventPreviewAPI:
    'https://mntg.carsprogram.org/events_v1/api/eventPreview/carsx',
};

export const DebuggingConfig = {
  showConsoleLogs: true,
};

if (window.location.hostname === 'chaos.irisx.org') {
  //  workaround for api domain rewriting for chaos server
  APIConfig.endpointURLBase = String('https://de').concat('v.irisx.org/api/');
} else {
  APIConfig.endpointURLBase =
    process.env.NODE_ENV === 'production'
      ? //  in production, the site targets the api relative to its webroot
        APIConfig.relativeAPIPath
      : //  in development, the site targets another instance of the backend somewhere (likely dev.irisx.org or test.irisx.org)
        APIHost['MN-PROD'];
}

export { APIConfig };

export enum SVGIconPaths {
  INFO = 'img/event-type-icon-info.svg',
  PLACEHOLDER = 'img/icon-placeholder-fill-solid.svg',
}

export enum PNGIconPaths {
  INFO = 'img/event-type-icon-info.png',
  PLACEHOLDER = 'img/icon-placeholder-fill-solid.png',
  PLACEHOLDER_LARGE = 'img/icon-placeholder-fill-solid-120.png',
}

export const DateOptions: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  month: 'numeric',
  day: '2-digit',
  year: 'numeric',
  timeZoneName: 'short',
};

export const jwtKeyName = () => `${window.location.host}_CARSX_JWT`;
